/**
 * Dashboard State Management Hook
 *
 * This hook encapsulates all dashboard state logic and will be easily
 * adaptable for future backend integration.
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import type { DashboardTab, DropdownState, MetricData, SalesMetric } from '../types/DashboardTypes';
import { DASHBOARD_METRICS } from '../constants/DashboardData';
import type { KpiSummary, KpiMetric } from 'model/salesDashboard';

const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatChangePct = (changePct: number | undefined): string => {
    if (changePct == null) return '0%';
    const sign = changePct >= 0 ? '+' : '';
    return `${sign}${changePct.toFixed(1)}%`;
};

const kpiMetricToSalesMetric = (metric: KpiMetric, title: string): SalesMetric => ({
    title,
    value: formatCurrency(metric.amount),
    change: formatChangePct(metric.changePct),
    avgPrice: formatCurrency(metric.avgPrice)
});

const kpiSummaryToMetrics = (kpiSummary: KpiSummary): SalesMetric[] => [
    kpiMetricToSalesMetric(kpiSummary.totalSales, 'Total sales'),
    kpiMetricToSalesMetric(kpiSummary.grooming, 'Grooming services'),
    kpiMetricToSalesMetric(kpiSummary.daycare, 'Daycare services'),
    kpiMetricToSalesMetric(kpiSummary.boarding, 'Boarding services'),
    kpiMetricToSalesMetric(kpiSummary.addons, 'Add-ons'),
    kpiMetricToSalesMetric(kpiSummary.products, 'Products'),
    kpiMetricToSalesMetric(kpiSummary.customItems, 'Custom items')
];

export const useDashboardState = () => {
    const [selectedTab, setSelectedTab] = useState<DashboardTab>('sales');

    const [expandedDropdowns, setExpandedDropdowns] = useState<DropdownState>({
        year: false,
        comparison: false,
        businessType: false,
        region: false,
        location: false
    });

    const kpiSummary = useSelector((state: RootState) => state.salesDashboard.kpiSummary);

    const handleDropdownToggle = (dropdownId: keyof DropdownState) => {
        setExpandedDropdowns(prev => {
            const allClosed = Object.keys(prev).reduce((acc, key) => {
                acc[key as keyof DropdownState] = false;
                return acc;
            }, {} as DropdownState);

            return {
                ...allClosed,
                [dropdownId]: !prev[dropdownId]
            };
        });
    };

    const handleDropdownClose = (dropdownId: keyof DropdownState) => {
        setExpandedDropdowns(prev => ({
            ...prev,
            [dropdownId]: false
        }));
    };

    const getMetricsData = (): MetricData[] => {
        if (selectedTab === 'sales' && kpiSummary) {
            return kpiSummaryToMetrics(kpiSummary);
        }
        return DASHBOARD_METRICS[selectedTab] || [];
    };

    return {
        selectedTab,
        setSelectedTab,
        expandedDropdowns,
        handleDropdownToggle,
        handleDropdownClose,
        getMetricsData
    };
};
